if (!customElements.get('instagram-feed')) {
  customElements.define(
    'instagram-feed',
    class InstagramFeed extends HTMLElement {
      constructor() {
        super();

        this.onBreakpointChange = this.onBreakpointChange.bind(this);
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.testimonials__items',
          pagination: '.swiper-pagination',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          status: '[data-instagram-status]',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);
        this.status = this.section && this.section.querySelector(this.selectors.status);
        this.closeIcon = this.querySelector('[data-instagram-close-icon]')?.innerHTML.trim() || '&times;';

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items || 1, 10);
        this.laptopItems = parseInt(this.dataset.laptopItems || this.items, 10);
        this.tabletItems = parseInt(this.dataset.tabletItems || 2, 10);
        this.layout = this.dataset.layout || 'card';
        this.swipeMobile = this.dataset.swipeMobile === 'true';
        this.limit = parseInt(this.dataset.limit || 60, 10);
        this.paginationType = this.dataset.paginationType || 'fraction';
        this.sectionColorScheme = this.dataset.sectionColorScheme || '';
        this.cardColorScheme = this.dataset.cardColorScheme || 'scheme-inverse';
        this.cardContentColorScheme = this.dataset.cardContentColorScheme || 'scheme-1';
        this.cardTextSize = this.dataset.cardTextSize || 'text-base';
        this.cardHeadingSize = this.dataset.cardHeadingSize || 'h5';
        this.cardImageRatio = this.dataset.cardImageRatio || '1/1';
        this.cardTriggerBehavior = this.dataset.cardTriggerBehavior || 'click';
        this.shopNowLabel = this.dataset.shopNowLabel || 'Shop now';
        this.viewOnInstagramLabel = this.dataset.viewOnInstagramLabel || 'View on Instagram';

        this.feed = [];
        this.sliderInstance = false;

        document.addEventListener('matchMobile', this.onBreakpointChange);
        document.addEventListener('unmatchMobile', this.onBreakpointChange);

        this.loadFeed();
      }

      disconnectedCallback() {
        document.removeEventListener('matchMobile', this.onBreakpointChange);
        document.removeEventListener('unmatchMobile', this.onBreakpointChange);
        this.destroySlider();
      }

      get feedUrl() {
        const locale = (this.dataset.locale || '').toLowerCase();
        if (locale.startsWith('fr') && this.dataset.feedUrlFr) return this.dataset.feedUrlFr;
        return this.dataset.feedUrl;
      }

      async loadFeed() {
        if (!this.feedUrl || !this.sliderWrapper) return;

        try {
          const response = await fetch(this.feedUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Instagram feed failed with ${response.status}`);

          const json = await response.json();
          const posts = Array.isArray(json) ? json : Object.values(json);
          this.feed = posts.filter(Boolean).slice(0, this.limit);

          if (!this.feed.length) {
            this.showStatus('Instagram feed is empty.');
            return;
          }

          this.renderFeed();
        } catch (error) {
          this.showStatus('Instagram feed is unavailable.');
          console.error(error);
        }
      }

      renderFeed() {
        this.sliderWrapper.innerHTML = this.feed.map((post, index) => this.renderPost(post, index)).join('');
        this.refreshSlider();
        this.prepareVideos();
      }

      renderPost(post, index) {
        if (this.layout === 'card') return this.renderCardPost(post, index);
        if (this.layout === 'focused') return this.renderSplitPost(post, index, 'testimonial--layout-1');
        return this.renderSplitPost(post, index, 'testimonial--layout-2');
      }

      renderCardPost(post, index) {
        const colorClass = `color-${this.cardColorScheme}`;
        const contentColorClass = `color-${this.cardContentColorScheme}`;
        const colorInheritMobileClass =
          this.sectionColorScheme === this.cardContentColorScheme ? ' testimonial--color-inherit-mobile' : '';
        const heading = this.getHeading(post);
        const caption = this.renderCaption(post, true);
        const products = this.renderProducts(post);
        const instagramLink = this.renderInstagramLink(post);

        return `
          <div class="f-column${this.enableSlider ? ' swiper-slide' : ''}" data-index="${index}">
            <testimonial-layered
              class="testimonial testimonial--layout-3 relative h-full flex flex-col${colorInheritMobileClass}"
              data-trigger="${this.escapeAttr(this.cardTriggerBehavior)}"
              data-color-scheme="${this.escapeAttr(colorClass)}"
              data-content-color-scheme="${this.escapeAttr(contentColorClass)}"
            >
              ${this.renderMedia(post, 'testimonial__image h-full media-wrapper blocks-radius relative')}
              <div class="testimonial__wrapper flex items-end justify-center relative z-1 blocks-radius has-overlay ${colorClass}">
                <div class="testimonial__inner ${colorClass} mobile-color-${this.escapeAttr(this.cardContentColorScheme)} blocks-radius">
                  <div class="testimonial__header relative" role="button">
                    <h3 class="testimonial__heading ${this.escapeAttr(this.cardHeadingSize)}">${heading}</h3>
                    <button class="testimonial__toggle hidden md:flex" type="button" aria-label="Close">
                      ${this.closeIcon}
                    </button>
                  </div>
                  <div class="testimonial__summary">
                    <div class="testimonial__summary-inner overflow-hidden">
                      ${caption}
                      ${products}
                      ${instagramLink}
                    </div>
                  </div>
                </div>
              </div>
            </testimonial-layered>
          </div>
        `;
      }

      renderSplitPost(post, index, layoutClass) {
        const hasBg = this.sectionColorScheme !== this.cardColorScheme ? ' testimonial--has-bg blocks-radius' : '';
        const colorClass = `color-${this.cardColorScheme}`;
        const caption = this.renderCaption(post, false);
        const products = this.renderProducts(post);
        const instagramLink = this.renderInstagramLink(post);

        return `
          <div class="f-column${this.enableSlider ? ' swiper-slide' : ''}" data-index="${index}">
            <div class="testimonial ${layoutClass}${hasBg} ${colorClass} h-full flex flex-col">
              <div class="testimonial__wrapper">
                ${this.renderMedia(post, 'testimonial__image media-wrapper blocks-radius')}
                <div class="testimonial__inner">
                  ${caption}
                  ${instagramLink}
                </div>
              </div>
              ${products}
            </div>
          </div>
        `;
      }

      renderMedia(post, className) {
        const mediaUrl = post.media_url || '';
        const mediaType = (post.media_type || '').toUpperCase();
        const ratio = this.cardImageRatio === 'adapt' ? '1/1' : this.cardImageRatio;
        const alt = this.getHeading(post);
        const media =
          mediaType === 'VIDEO'
            ? `<video class="instagram-feed__media motion-reduce" src="${this.escapeAttr(
                mediaUrl
              )}" autoplay muted loop playsinline preload="auto" data-instagram-video data-instagram-video-ready="false" aria-label="${this.escapeAttr(
                alt
              )}"></video>`
            : `<img class="instagram-feed__media motion-reduce" src="${this.escapeAttr(
                mediaUrl
              )}" loading="lazy" alt="${this.escapeAttr(alt)}">`;

        return `
          <div class="${className}">
            <motion-element data-motion="zoom-out-sm" class="block h-full" style="--aspect-ratio: ${this.escapeAttr(
              ratio
            )};">
              ${media}
            </motion-element>
          </div>
        `;
      }

      prepareVideos() {
        this.querySelectorAll('video[data-instagram-video]').forEach((video) => {
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = 'auto';
          video.dataset.instagramVideoReady = 'false';

          const revealVideo = () => {
            if (video.dataset.instagramVideoReady === 'true') return;
            video.dataset.instagramVideoReady = 'true';
            video.classList.add('is-ready');
          };

          const revealAfterFrame = () => {
            if ('requestVideoFrameCallback' in video) {
              const fallback = window.setTimeout(revealVideo, 250);
              video.requestVideoFrameCallback(() => {
                window.clearTimeout(fallback);
                revealVideo();
              });
              return;
            }

            revealVideo();
          };

          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            revealAfterFrame();
          } else {
            video.addEventListener('loadeddata', revealAfterFrame, { once: true });
            video.addEventListener('canplay', revealAfterFrame, { once: true });
          }

          video.load();

          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        });
      }

      renderCaption(post, nested) {
        if (!post.caption) return '';

        const content = `<p>${this.escapeHtml(post.caption).replace(/\r?\n/g, '<br>')}</p>`;
        if (nested) {
          return `
            <div class="testimonial__content overflow-hidden">
              <div class="testimonial__text rte ${this.escapeAttr(this.cardTextSize)}${
                this.cardTextSize === 'text-base' ? ' text-subtext' : ''
              }">
                ${content}
              </div>
            </div>
          `;
        }

        return `
          <div class="testimonial__content rte ${this.escapeAttr(this.cardTextSize)}${
            this.cardTextSize === 'text-base' ? ' text-subtext' : ''
          }">
            ${content}
          </div>
        `;
      }

      renderProducts(post) {
        const products = Array.isArray(post.products) ? post.products.filter(Boolean) : [];
        if (!products.length) return '';

        const productRows = products.map((product) => this.renderProduct(product)).join('');
        return `<div class="testimonial__footer instagram-feed__products">${productRows}</div>`;
      }

      renderProduct(product) {
        const title = product.title || product.sku || this.shopNowLabel;
        const buttonLabel = product.image ? this.shopNowLabel : product.button_text || this.shopNowLabel;
        const sku = product.sku
          ? `<small class="instagram-feed__product-meta text-subtext">${this.escapeHtml(product.sku)}</small>`
          : '';
        const image = product.image
          ? `
            <span class="block blocks-radius-sm shrink-0" style="--aspect-ratio: 1;">
              <img class="instagram-feed__media motion-reduce" src="${this.escapeAttr(
                product.image
              )}" loading="lazy" alt="${this.escapeAttr(title)}">
            </span>
          `
          : '';

        return `
          <a class="testimonial__product instagram-feed__product" href="${this.escapeAttr(product.url || '#')}" target="_top">
            ${image}
            <p class="testimonial__product-title m-0">
              <span class="reversed-link">${this.escapeHtml(title)}</span>
              ${sku}
              <small class="instagram-feed__product-action text-subtext">${this.escapeHtml(buttonLabel)}</small>
            </p>
          </a>
        `;
      }

      renderInstagramLink(post) {
        if (!post.permalink) return '';

        return `
          <a class="instagram-feed__instagram-link btn btn--plain" href="${this.escapeAttr(
            post.permalink
          )}" target="_blank" rel="noopener">
            <span class="btn__text">${this.escapeHtml(this.viewOnInstagramLabel)}</span>
          </a>
        `;
      }

      getHeading(post) {
        const caption = post.caption || '';
        const firstLine = caption
          .split(/\r?\n/)
          .map((line) => line.trim())
          .find(Boolean);
        const heading = firstLine || post.shortcode || '#stevesmusic';
        return this.escapeHtml(heading.length > 120 ? `${heading.slice(0, 117)}...` : heading);
      }

      showStatus(message) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.hidden = false;
      }

      onBreakpointChange() {
        this.refreshSlider();
      }

      refreshSlider() {
        if (!this.enableSlider) return;

        if (FoxTheme.config.mqlMobile && this.swipeMobile) {
          this.destroySlider();
        } else if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.update();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (!this.sliderWrapper || typeof this.sliderInstance === 'object') return;

        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
        const columnGapMobile = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap-mobile');
        const spaceBetween = this.toPixels(columnGap);
        const spaceBetweenMobile = this.toPixels(columnGapMobile);
        let additionalModules = [];

        const defaultOptions = {
          slidesPerView: 1,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems > 3 ? 3 : this.tabletItems,
            },
            1024: {
              slidesPerView: this.laptopItems > 3 ? 3 : this.laptopItems,
            },
            1280: {
              slidesPerView: this.items,
            },
          },
          loop: false,
          threshold: 2,
        };

        let settings = {};
        if (this.layout === 'focused') {
          settings = {
            centeredSlides: true,
            spaceBetween: spaceBetweenMobile,
            pagination: {
              el: this.section.querySelector(this.selectors.pagination),
              clickable: true,
              type: this.paginationType,
            },
            loop: true,
            breakpoints: {
              768: {
                slidesPerView: 2,
                spaceBetween: spaceBetween,
              },
              1280: {
                slidesPerView: Math.max(2, this.getSlidesPerViewCentered(this.laptopItems)),
                spaceBetween: spaceBetween,
              },
              1440: {
                slidesPerView: Math.max(2, this.getSlidesPerViewCentered(this.items)),
                spaceBetween: spaceBetween,
              },
            },
          };
        } else {
          additionalModules.push(FoxTheme.Swiper.Mousewheel);
          settings = {
            mousewheel: {
              enabled: true,
              forceToAxis: true,
              thresholdDelta: 10,
              thresholdTime: 100,
            },
          };
        }

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, { ...defaultOptions, ...settings }, additionalModules);
        this.sliderInstance.init();
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper && this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper && this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      getSlidesPerViewCentered(number) {
        if (number % 2 !== 0) return number - 1;
        return number;
      }

      toPixels(value) {
        const numeric = parseFloat(value);
        if (Number.isNaN(numeric)) return 0;
        return value.includes('rem') ? numeric * 10 : numeric;
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }

      escapeAttr(value) {
        return this.escapeHtml(value);
      }
    }
  );
}